import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { purchasingAPI, ContractPrice, coreAPI } from '../../lib/api';
import { usePreferences } from '../../contexts/PreferencesContext';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface ContractPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: ContractPrice | null;
}

interface PartRow {
  id?: number;
  part_number: string;
  description: string;
  unit_price: string;
  lot_price: string;
  qty_from: string;
  qty_to: string;
  unit_of_measure: string;
}

interface Company {
  id: number;
  name: string;
  company_type: string;
}

export default function ContractPriceModal({ isOpen, onClose, contract }: ContractPriceModalProps) {
  const isEditing = !!contract;
  const { preferences } = usePreferences();
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);

  // Form state — default currency from user preferences
  const [formData, setFormData] = useState({
    contract_number: '',
    customer: '',
    supplier: '',
    valid_from: '',
    valid_to: '',
    currency: preferences.currency || 'GBP',
    status: 'draft',
    notes: '',
  });

  const [parts, setParts] = useState<PartRow[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await coreAPI.getCompanies({ page_size: 1000 });
        setCompanies(response.data.results || []);
      } catch (err) {
        console.error('Failed to load companies:', err);
      }
    };
    fetchCompanies();
  }, []);

  // Load contract data when editing
  useEffect(() => {
    if (contract) {
      setFormData({
        contract_number: contract.contract_number,
        customer: String(contract.customer),
        supplier: String(contract.supplier),
        valid_from: contract.valid_from,
        valid_to: contract.valid_to,
        currency: contract.currency,
        status: contract.status,
        notes: contract.notes || '',
      });

      // Load parts
      if (contract.parts && contract.parts.length > 0) {
        setParts(contract.parts.map(p => ({
          id: p.id,
          part_number: p.part_number,
          description: p.description || '',
          unit_price: p.unit_price || '',
          lot_price: p.lot_price || '',
          qty_from: p.qty_from,
          qty_to: p.qty_to,
          unit_of_measure: p.unit_of_measure,
        })));
      }
    } else {
      // Reset form for new contract
      setFormData({
        contract_number: '',
        customer: '',
        supplier: '',
        valid_from: '',
        valid_to: '',
        currency: preferences.currency || 'GBP',
        status: 'draft',
        notes: '',
      });
      setParts([]);
    }
  }, [contract]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addPart = () => {
    setParts(prev => [...prev, {
      part_number: '',
      description: '',
      unit_price: '',
      lot_price: '',
      qty_from: '0',
      qty_to: '0',
      unit_of_measure: 'EA',
    }]);
  };

  const removePart = (index: number) => {
    setParts(prev => prev.filter((_, i) => i !== index));
  };

  const updatePart = (index: number, field: string, value: string) => {
    setParts(prev => prev.map((part, i) =>
      i === index ? { ...part, [field]: value } : part
    ));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.contract_number.trim()) {
      newErrors['contract_number'] = 'Contract number is required';
    }
    if (!formData.customer) {
      newErrors['customer'] = 'Customer is required';
    }
    // CR-5477: backend ContractPrice.supplier is non-nullable
    // (on_delete=PROTECT); submitting without it returned a 400 the
    // form silently rolled into a generic "Failed to save" toast,
    // blocking the entire Price Book → Contract setup workflow.
    if (!formData.supplier) {
      newErrors['supplier'] = 'Supplier is required';
    }
    if (!formData.valid_from) {
      newErrors['valid_from'] = 'Valid from date is required';
    }
    if (!formData.valid_to) {
      newErrors['valid_to'] = 'Valid to date is required';
    }
    if (formData.valid_from && formData.valid_to && formData.valid_from > formData.valid_to) {
      newErrors['valid_to'] = 'Valid to must be after valid from';
    }

    // Validate parts
    parts.forEach((part, index) => {
      if (!part.part_number.trim()) {
        newErrors[`part_${index}_part_number`] = 'Part number is required';
      }
      if (!part.unit_price && !part.lot_price) {
        newErrors[`part_${index}_price`] = 'Either unit price or lot price is required';
      }
      if (parseFloat(part.qty_from) > parseFloat(part.qty_to)) {
        newErrors[`part_${index}_qty`] = 'Qty from must be <= qty to';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error('Please fix the validation errors');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        contract_number: formData.contract_number,
        customer: parseInt(formData.customer),
        supplier: formData.supplier ? parseInt(formData.supplier) : undefined,
        valid_from: formData.valid_from,
        valid_to: formData.valid_to,
        currency: formData.currency,
        status: formData.status as 'draft' | 'active' | 'expired',
        notes: formData.notes,
        parts: parts.map(p => ({
          ...(p.id ? { id: p.id } : {}),
          part_number: p.part_number,
          description: p.description,
          unit_price: p.unit_price || null,
          lot_price: p.lot_price || null,
          qty_from: p.qty_from,
          qty_to: p.qty_to,
          unit_of_measure: p.unit_of_measure,
        })),
      };

      if (isEditing && contract) {
        await purchasingAPI.updateContractPrice(contract.id, payload);
        toast.success('Contract updated successfully');
      } else {
        await purchasingAPI.createContractPrice(payload);
        toast.success('Contract created successfully');
      }

      onClose();
    } catch (err: unknown) {
      console.error('Failed to save contract:', err);
      toast.error(getApiErrorMessage(err, 'Failed to save contract'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const customers = companies.filter(c => c.company_type === 'customer');
  const suppliers = companies.filter(c => c.company_type === 'supplier');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {isEditing ? 'Edit Contract Price' : 'Add Contract Price'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Contract Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Contract Number */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Contract Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.contract_number}
                onChange={(e) => handleInputChange('contract_number', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${errors['contract_number'] ? 'border-red-500' : 'border-border'}`}
                placeholder="CP-001"
              />
              {errors['contract_number'] && (
                <p className="text-xs text-red-500 mt-1">{errors['contract_number']}</p>
              )}
            </div>

            {/* Customer */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Customer <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.customer}
                onChange={(e) => handleInputChange('customer', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${errors['customer'] ? 'border-red-500' : 'border-border'}`}
              >
                <option value="">Select Customer</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {errors['customer'] && (
                <p className="text-xs text-red-500 mt-1">{errors['customer']}</p>
              )}
            </div>

            {/* Supplier */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Supplier <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.supplier}
                onChange={(e) => handleInputChange('supplier', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${errors['supplier'] ? 'border-red-500' : 'border-border'}`}
              >
                <option value="">Select Supplier</option>
                {suppliers.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {errors['supplier'] && (
                <p className="text-xs text-red-500 mt-1">{errors['supplier']}</p>
              )}
            </div>

            {/* Valid From */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Valid From <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.valid_from}
                onChange={(e) => handleInputChange('valid_from', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${errors['valid_from'] ? 'border-red-500' : 'border-border'}`}
              />
              {errors['valid_from'] && (
                <p className="text-xs text-red-500 mt-1">{errors['valid_from']}</p>
              )}
            </div>

            {/* Valid To */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Valid To <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.valid_to}
                onChange={(e) => handleInputChange('valid_to', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${errors['valid_to'] ? 'border-red-500' : 'border-border'}`}
              />
              {errors['valid_to'] && (
                <p className="text-xs text-red-500 mt-1">{errors['valid_to']}</p>
              )}
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-medium mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="GBP">GBP - British Pound</option>
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="JPY">JPY - Japanese Yen</option>
                <option value="CNY">CNY - Chinese Yuan</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                rows={2}
                placeholder="Optional notes about this contract..."
              />
            </div>
          </div>

          {/* Parts Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Contract Parts</h3>
              <Button variant="outline" size="sm" onClick={addPart}>
                <Plus className="h-4 w-4 mr-1" />
                Add Part
              </Button>
            </div>

            {parts.length === 0 ? (
              <div className="text-center py-8 bg-muted/30 rounded-lg border border-dashed">
                <p className="text-muted-foreground text-sm">No parts added yet</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={addPart}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add First Part
                </Button>
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-2 px-3 font-medium min-w-[140px]">Part Number *</th>
                      <th className="text-left py-2 px-3 font-medium min-w-[160px]">Description</th>
                      <th className="text-right py-2 px-3 font-medium min-w-[120px]">Unit Price</th>
                      <th className="text-right py-2 px-3 font-medium min-w-[120px]">Lot Price</th>
                      <th className="text-right py-2 px-3 font-medium min-w-[110px]">Qty From *</th>
                      <th className="text-right py-2 px-3 font-medium min-w-[110px]">Qty To *</th>
                      <th className="text-center py-2 px-3 font-medium min-w-[80px]">UOM</th>
                      <th className="text-center py-2 px-3 font-medium w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parts.map((part, index) => (
                      <tr key={index} className="border-b last:border-b-0">
                        <td className="py-2 px-3">
                          <input
                            type="text"
                            value={part.part_number}
                            onChange={(e) => updatePart(index, 'part_number', e.target.value)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary ${errors[`part_${index}_part_number`] ? 'border-red-500' : 'border-border'}`}
                            placeholder="PART-001"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="text"
                            value={part.description}
                            onChange={(e) => updatePart(index, 'description', e.target.value)}
                            className="w-full px-2 py-1 border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                            placeholder="Description"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            step="0.0001"
                            value={part.unit_price}
                            onChange={(e) => updatePart(index, 'unit_price', e.target.value)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary text-right ${errors[`part_${index}_price`] ? 'border-red-500' : 'border-border'}`}
                            placeholder="0.00"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            step="0.0001"
                            value={part.lot_price}
                            onChange={(e) => updatePart(index, 'lot_price', e.target.value)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary text-right ${errors[`part_${index}_price`] ? 'border-red-500' : 'border-border'}`}
                            placeholder="0.00"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            value={part.qty_from}
                            onChange={(e) => updatePart(index, 'qty_from', e.target.value)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary text-right ${errors[`part_${index}_qty`] ? 'border-red-500' : 'border-border'}`}
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            value={part.qty_to}
                            onChange={(e) => updatePart(index, 'qty_to', e.target.value)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary text-right ${errors[`part_${index}_qty`] ? 'border-red-500' : 'border-border'}`}
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <select
                            value={part.unit_of_measure}
                            onChange={(e) => updatePart(index, 'unit_of_measure', e.target.value)}
                            className="px-2 py-1 border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                          >
                            <option value="EA">EA</option>
                            <option value="KG">KG</option>
                            <option value="M">M</option>
                            <option value="L">L</option>
                            <option value="PC">PC</option>
                            <option value="SET">SET</option>
                          </select>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <button
                            onClick={() => removePart(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              isEditing ? 'Update Contract' : 'Create Contract'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
